import type {
    ComponentProps,
    InputHTMLAttributes,
    MutableRefObject,
    ReactElement,
    RefObject,
} from 'react';
import { forwardRef, useContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import type { ControllerRenderProps, FieldError } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { isNil } from 'lodash-es';
import type { InputProps } from '../../form/Input';
import Input from '../../form/Input';
import { FormFieldContext, useOverrideState, useSharedFormContext } from '../utils';
import FormFieldContainer from './FormFieldContainer';
import type { FieldOptions } from '../types';

export function isNumberType(type: string): boolean {
    return ['integer', 'float'].includes(type);
}

export function fieldTypeToInputType(type: string): InputHTMLAttributes<HTMLInputElement>['type'] {
    return isNumberType(type) ? 'number' : undefined;
}

interface OverridableWrapperProps {
    render: (props: Record<string, unknown>) => ReactElement;
    disabled: boolean;
    overrideName: string;
}

export function OverridableWrapper({
    render,
    disabled,
    overrideName,
}: OverridableWrapperProps): ReactElement {
    const { setHover } = useContext(FormFieldContext)!;
    const { formController } = useSharedFormContext();

    // Unset hover when form field turns to disabled=false.
    useEffect(() => {
        if (!disabled) {
            setHover(false);
        }
    }, [disabled, setHover]);

    const extraInputProps = useMemo(() => {
        const props: Record<string, unknown> = {};

        if (disabled) {
            props.onMouseOver = (): void => {
                setHover(true);
            };
            props.onMouseLeave = (): void => {
                setHover(false);
            };
            props.onClick = (): void => {
                formController.askAreYouSureToOverrideMessageOnce() &&
                    formController.setOverriddenField(overrideName, true);
            };
            props.readOnly = true;
        }

        return props;
    }, [disabled, setHover, overrideName, formController]);

    return render(extraInputProps);
}
OverridableWrapper.propTypes = {
    disabled: PropTypes.bool.isRequired,
    overrideName: PropTypes.string.isRequired,
};

interface OverridableInputProps {
    id: string;
    disabled: boolean;
    overrideName: string;
    overridableByProjectSettings: true;
}

type NonOverridableInputProps = ComponentProps<typeof Input> & {
    overridableByProjectSettings?: false;
};

/**
 * Wrapper around Input with some helpers for overridable fields.
 */
export const OverridableInput = forwardRef<
    HTMLInputElement,
    OverridableInputProps | NonOverridableInputProps
>(({ overridableByProjectSettings = true, ...otherProps }, ref) => {
    const { disabled, overrideName, ...inputProps } = otherProps as OverridableInputProps;
    if (overridableByProjectSettings) {
        return (
            <OverridableWrapper
                disabled={disabled}
                overrideName={overrideName}
                render={(props) => <Input ref={ref} {...inputProps} {...props} />}
            />
        );
    }
    return <Input ref={ref} {...inputProps} />;
});

OverridableInput.displayName = 'OverridableInput';

interface InputFieldProps {
    InjectableElement?: ({
        inputRef,
        fieldOptions,
        disabled,
    }: {
        inputRef: RefObject<HTMLInputElement>;
        fieldOptions: FieldOptions;
        disabled: boolean;
    }) => ReactElement;
    fieldOptions: FieldOptions;
    extraFormFieldContainerProps?: Record<string, unknown>;
}

function InputField({
    InjectableElement,
    fieldOptions,
    extraFormFieldContainerProps,
}: InputFieldProps): ReactElement {
    const { name, settings } = fieldOptions;
    const {
        type,
        placeholder,
        step,
        required,
        maxLength,
        minValue,
        maxValue,
        disableWithZero,
        unitConversion,
    } = settings!;

    const { overridable, overridden, disabled: overrideDisabled } = useOverrideState(name);

    // Check if this field is explicitly disabled in views.py if so, it
    // cannot be enabled by project overrides.
    const disabled = settings?.readOnly || overrideDisabled;
    const overridableByProjectSettings = !settings?.readOnly && overridable && !overridden;
    const inputType = useMemo(() => fieldTypeToInputType(type), [type]);
    const inputRef: MutableRefObject<HTMLInputElement | null> = useRef(null);

    return (
        <FormFieldContainer
            fieldOptions={fieldOptions}
            {...extraFormFieldContainerProps}
            inline={true}
        >
            {InjectableElement && (
                <InjectableElement
                    inputRef={inputRef}
                    fieldOptions={fieldOptions}
                    disabled={disabled}
                />
            )}
            <Controller
                name={name}
                defaultValue={settings?.default}
                rules={{
                    required,
                    maxLength,
                    // React hook form types are not up to date with the latest version,
                    // hence `minValue` is not recognized.
                    // TODO: Remove the this when we upgrade to a version that includes it.
                    // [https://github.com/omnidots/website/issues/8939]
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    minValue,
                    maxValue,
                    type,
                    disableWithZero,
                    unitConversion,
                }}
                render={({
                    field: { ref, value, onChange, ...formHookProps },
                    fieldState: { error },
                }: {
                    field: ControllerRenderProps<Record<string, unknown>, string>;
                    fieldState: { error?: FieldError };
                }) => (
                    <OverridableInput
                        id={`id_${name}`}
                        ref={(e: HTMLInputElement | null) => {
                            ref(e);
                            inputRef.current = e;
                        }}
                        error={error}
                        disabled={disabled}
                        overrideName={name}
                        overridableByProjectSettings={overridableByProjectSettings}
                        placeholder={placeholder ?? ''}
                        step={step}
                        type={inputType}
                        value={isNil(value) ? '' : (value as InputProps['value'])}
                        onChange={({
                            target: { value: newValue },
                        }: {
                            target: { value: string };
                        }): void => {
                            if (newValue === '') {
                                onChange(null);
                                return;
                            }

                            if (type === 'float') {
                                onChange(parseFloat(newValue.replace(',', '.')));
                                return;
                            }

                            if (type === 'integer') {
                                onChange(parseInt(newValue, 10));
                                return;
                            }

                            onChange(newValue);
                        }}
                        {...formHookProps}
                    />
                )}
            />
        </FormFieldContainer>
    );
}

export default InputField;
